import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Save, X, RefreshCw, Target } from 'lucide-react';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';
import { useToast, ToastContainer } from '../common/Toast';
import MasterDataService from '../../services/masterDataService';

const CreateCategory = () => {
  const navigate = useNavigate();
  const { success, error: showError, toasts, removeToast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [existingCategories, setExistingCategories] = useState([]);

  const [formData, setFormData] = useState({
    categoryName: '',
    categoryCode: '',
    parentCategory: '',
    categoryType: 'Equipment',
    description: '',
    subcategories: '',
    depreciationRate: 20,
    usefulLife: 5,
    status: 'Active',
    tags: '',
    notes: ''
  });

  // Load existing categories for parent category dropdown
  useEffect(() => {
    loadExistingCategories();
  }, []);

  const loadExistingCategories = async () => {
    try {
      const response = await MasterDataService.getByCategory('AssetType');
      const categories = response?.data || response || [];
      setExistingCategories(Array.isArray(categories) ? categories : []);
    } catch (err) {
      console.warn('Could not load existing categories:', err);
    }
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      // Validate required fields
      if (!formData.categoryName || formData.categoryName.trim() === '') {
        showError('Category name is required');
        setIsLoading(false);
        return;
      }

      const categoryData = {
        category: 'AssetType',
        code: formData.categoryCode || formData.categoryName.substring(0, 5).toUpperCase().replace(/\s+/g, '_'),
        label: formData.categoryName.trim(),
        description: formData.description?.trim() || '',
        parentValue:
          formData.parentCategory &&
          formData.parentCategory !== 'Root' &&
          formData.parentCategory !== ''
            ? formData.parentCategory
            : null,
        isActive: formData.status === 'Active',
        sortOrder: existingCategories.length + 1,
        notes: formData.notes?.trim() || '',
        metadata: {
          categoryType: formData.categoryType,
          depreciationRate: formData.depreciationRate,
          usefulLife: formData.usefulLife,
          subcategories: formData.subcategories,
          tags: formData.tags
        }
      };

      const response = await MasterDataService.create(categoryData);

      if (response && response.success) {
        // Create subcategories if provided
        if (formData.subcategories && formData.subcategories.trim() !== '') {
          const subs = formData.subcategories
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean);
          for (const sub of subs) {
            const subData = {
              category: 'AssetSubtype',
              code:
                sub.substring(0, 5).toUpperCase().replace(/\s+/g, '_') +
                '_' +
                Math.floor(Math.random() * 1000),
              label: sub,
              description: `Subcategory of ${formData.categoryName.trim()}`,
              parentValue: categoryData.label,
              isActive: true,
              sortOrder: 1,
              metadata: {}
            };
            try {
              await MasterDataService.create(subData);
            } catch (subErr) {
              console.warn(`Failed to create subcategory ${sub}:`, subErr);
            }
          }
        }

        success('Category created successfully!');
        navigate('/assets/categorization');
      } else {
        const errorMsg =
          response?.message || response?.error?.message || 'Failed to create category';
        throw new Error(errorMsg);
      }
    } catch (err) {
      console.error('Error creating category:', err);
      let errorMessage = 'Failed to save category. Please try again.';
      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.response?.data?.error) {
        errorMessage = err.response.data.error;
      } else if (err.message) {
        errorMessage = err.message;
      }
      showError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/assets/categorization');
  };

  const inputStyle = {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    fontSize: '14px',
    fontFamily: 'var(--font-family)',
    backgroundColor: 'white',
    boxSizing: 'border-box'
  };

  const labelStyle = {
    display: 'block',
    fontSize: '14px',
    fontWeight: '500',
    color: '#374151',
    marginBottom: '6px',
    fontFamily: 'var(--font-family)'
  };

  const headerActions = [
    {
      label: 'Cancel',
      icon: <X size={18} />,
      onClick: handleCancel,
      variant: 'outline',
      tooltip: 'Cancel and go back'
    }
  ];

  return (
    <>
      <PageBreadcrumb />
      <PageHeader
        title="Add New Category"
        description="Create a new asset category with classification details and depreciation settings"
        actions={headerActions}
      />

      <div className="page-container" style={{ width: '100%', maxWidth: '100%' }}>
        <div style={{ width: '100%', margin: '0 auto' }}>
          <form onSubmit={handleFormSubmit}>
            {/* Basic Information Card */}
            <div
              style={{
                backgroundColor: 'white',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.07)',
                marginBottom: '24px',
                overflow: 'hidden'
              }}
            >
              {/* Card Header */}
              <div
                style={{
                  padding: '16px 24px',
                  borderBottom: '1px solid #e5e7eb',
                  backgroundColor: '#f8f9fa',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px'
                }}
              >
                <Target size={18} style={{ color: 'var(--primary-color)' }} />
                <h3
                  style={{
                    margin: 0,
                    fontSize: '15px',
                    fontWeight: '600',
                    color: '#111827',
                    fontFamily: 'var(--font-family)'
                  }}
                >
                  Category Information
                </h3>
              </div>

              {/* Card Body */}
              <div style={{ padding: '24px' }}>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(2, 1fr)',
                    gap: '20px'
                  }}
                >
                  {/* Category Name */}
                  <div>
                    <label style={labelStyle}>Category Name *</label>
                    <input
                      type="text"
                      value={formData.categoryName}
                      onChange={(e) =>
                        setFormData({ ...formData, categoryName: e.target.value })
                      }
                      required
                      placeholder="e.g., IT Equipment"
                      style={inputStyle}
                    />
                  </div>

                  {/* Category Code */}
                  <div>
                    <label style={labelStyle}>Category Code *</label>
                    <input
                      type="text"
                      value={formData.categoryCode}
                      onChange={(e) =>
                        setFormData({ ...formData, categoryCode: e.target.value })
                      }
                      required
                      placeholder="e.g., IT_EQ"
                      style={inputStyle}
                    />
                  </div>

                  {/* Parent Category */}
                  <div>
                    <label style={labelStyle}>Parent Category</label>
                    <select
                      value={formData.parentCategory}
                      onChange={(e) =>
                        setFormData({ ...formData, parentCategory: e.target.value })
                      }
                      style={inputStyle}
                    >
                      <option value="">None (Root Category)</option>
                      {existingCategories.map((cat, idx) => (
                        <option key={cat._id || idx} value={cat.label}>
                          {cat.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Category Type */}
                  <div>
                    <label style={labelStyle}>Category Type *</label>
                    <select
                      value={formData.categoryType}
                      onChange={(e) =>
                        setFormData({ ...formData, categoryType: e.target.value })
                      }
                      required
                      style={inputStyle}
                    >
                      <option value="Equipment">Equipment</option>
                      <option value="Furniture">Furniture</option>
                      <option value="Vehicle">Vehicle</option>
                      <option value="Property">Property</option>
                      <option value="License">License</option>
                    </select>
                  </div>

                  {/* Depreciation Rate */}
                  <div>
                    <label style={labelStyle}>Depreciation Rate (%) *</label>
                    <input
                      type="number"
                      value={formData.depreciationRate}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          depreciationRate: parseFloat(e.target.value) || 0
                        })
                      }
                      min="0"
                      max="100"
                      step="0.1"
                      required
                      style={inputStyle}
                    />
                  </div>

                  {/* Useful Life */}
                  <div>
                    <label style={labelStyle}>Useful Life (Years) *</label>
                    <input
                      type="number"
                      value={formData.usefulLife}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          usefulLife: parseInt(e.target.value) || 0
                        })
                      }
                      min="1"
                      required
                      style={inputStyle}
                    />
                  </div>

                  {/* Status */}
                  <div>
                    <label style={labelStyle}>Status *</label>
                    <select
                      value={formData.status}
                      onChange={(e) =>
                        setFormData({ ...formData, status: e.target.value })
                      }
                      required
                      style={inputStyle}
                    >
                      <option value="Active">Active</option>
                      <option value="Inactive">Inactive</option>
                      <option value="Archived">Archived</option>
                    </select>
                  </div>

                  {/* Subcategories - spans full width */}
                  <div style={{ gridColumn: '1 / -1' }}>
                    <label style={labelStyle}>Subcategories (Comma-separated)</label>
                    <input
                      type="text"
                      value={formData.subcategories}
                      onChange={(e) =>
                        setFormData({ ...formData, subcategories: e.target.value })
                      }
                      placeholder="e.g., Laptops, Desktops, Servers"
                      style={inputStyle}
                    />
                    <small
                      style={{
                        color: '#6b7280',
                        fontSize: '12px',
                        marginTop: '4px',
                        display: 'block'
                      }}
                    >
                      Enter comma-separated values to automatically create subcategories
                      under this category.
                    </small>
                  </div>

                  {/* Tags - spans full width */}
                  <div style={{ gridColumn: '1 / -1' }}>
                    <label style={labelStyle}>Tags (Comma-separated)</label>
                    <input
                      type="text"
                      value={formData.tags}
                      onChange={(e) =>
                        setFormData({ ...formData, tags: e.target.value })
                      }
                      placeholder="e.g., hardware, technology, depreciable"
                      style={inputStyle}
                    />
                  </div>

                  {/* Description - spans full width */}
                  <div style={{ gridColumn: '1 / -1' }}>
                    <label style={labelStyle}>Description</label>
                    <textarea
                      value={formData.description}
                      onChange={(e) =>
                        setFormData({ ...formData, description: e.target.value })
                      }
                      rows="3"
                      placeholder="Provide a description for this category..."
                      style={{ ...inputStyle, resize: 'vertical' }}
                    />
                  </div>

                  {/* Notes - spans full width */}
                  <div style={{ gridColumn: '1 / -1' }}>
                    <label style={labelStyle}>Notes</label>
                    <textarea
                      value={formData.notes}
                      onChange={(e) =>
                        setFormData({ ...formData, notes: e.target.value })
                      }
                      rows="3"
                      placeholder="Any additional notes..."
                      style={{ ...inputStyle, resize: 'vertical' }}
                    />
                  </div>
                </div>
              </div>

              {/* Form Actions - Now inside the card */}
              <div
                style={{
                  display: 'flex',
                  gap: '12px',
                  justifyContent: 'flex-end',
                  padding: '20px 24px',
                  backgroundColor: '#f8f9fa',
                  borderTop: '1px solid #e5e7eb'
                }}
              >
                <button
                  type="button"
                  onClick={handleCancel}
                  style={{
                    padding: '10px 24px',
                    fontSize: '14px',
                    fontWeight: '500',
                    fontFamily: 'var(--font-family)',
                    color: '#374151',
                    backgroundColor: 'white',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isLoading}
                  style={{
                    padding: '10px 24px',
                    fontSize: '14px',
                    fontWeight: '600',
                    fontFamily: 'var(--font-family)',
                    color: 'white',
                    backgroundColor: 'var(--primary-color)',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: isLoading ? 'not-allowed' : 'pointer',
                    opacity: isLoading ? 0.7 : 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  {isLoading ? (
                    <>
                      <RefreshCw size={16} style={{ animation: 'spin 1s linear infinite' }} />
                      Creating...
                    </>
                  ) : (
                    <>
                      <Save size={16} />
                      Create Category
                    </>
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>

      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </>
  );
};

export default CreateCategory;
